"use client";

import { FormInput } from "@client/components/forms/form-input";
import { FormRoleField } from "@client/components/forms/form-role-field";
import { getOrCreateUserId } from "@client/utils/getOrCreateUserId";
import { roundMemberRoleLabel } from "@lib/roundMemberRole";
import { createRoundWizardMember } from "@engine/round/actions";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Form,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sfc/ui";
import { ChevronLeftIcon, Plus, SendIcon, Trash2 } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { type RoundWizardMembersData } from "../RoundWizard";

const formSchema = createRoundWizardMember.omit({ id: true }).extend({
  email: z.string().email("Please enter a valid email address"),
});

type FormSchema = z.infer<typeof formSchema>;

interface RoundWizardMembersProps {
  userId: string;
  userInitials: string;
  members: RoundWizardMembersData;
  updateMembers: (members: RoundWizardMembersData) => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  onBack: () => void;
}

export function RoundWizardMembers({
  userId,
  userInitials,
  members,
  updateMembers,
  onSubmit,
  isSubmitting,
  onBack,
}: RoundWizardMembersProps) {
  const [isFetchingUserId, setIsFetchingUserId] = useState(false);
  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      roles: [],
      initials: "",
    },
  });

  async function handleAddMember(values: FormSchema) {
    if (members.some((member) => member.initials === values.initials)) {
      form.setError("initials", {
        type: "manual",
        message: "Initials already taken",
      });
      return;
    }

    if (members.some((member) => member.email === values.email)) {
      form.setError("email", {
        type: "manual",
        message: "Email already added",
      });
      return;
    }

    setIsFetchingUserId(true);

    try {
      const memberId = await getOrCreateUserId(values.email);

      if (userId === memberId) {
        if (!values.roles.includes("admin")) {
          form.setError("roles", {
            type: "manual",
            message: "You must be an admin",
          });
          return;
        }
      } else if (userInitials === values.initials) {
        form.setError("initials", {
          type: "manual",
          message: "This is your initials",
        });
        return;
      }

      updateMembers([
        ...members,
        { ...values, id: memberId, initials: values.initials.toUpperCase() },
      ]);

      form.reset({
        email: "",
        roles: [],
        initials: "",
      });
    } catch (error) {
      console.error(error);
      toast.error("Failed to add member. Please try again.");
    } finally {
      setIsFetchingUserId(false);
    }
  }

  function handleRemoveMember(index: number) {
    const newMembers = [...members];
    newMembers.splice(index, 1);
    updateMembers(newMembers);
  }

  const hasFunder = members.some((member) => member.roles.includes("funder"));

  function handleSubmit() {
    if (!hasFunder) {
      toast.error("Please add at least one funder");
      return;
    }

    onSubmit();
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Invite your team</CardTitle>
        <CardDescription>
          Invite others to participate in your funding round with different
          roles.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <Form {...form}>
          <form
            className="border rounded-md bg-muted p-4 flex flex-col gap-3"
            onSubmit={form.handleSubmit(handleAddMember)}
          >
            <FormInput
              className="grow"
              control={form.control}
              name="email"
              label="Email"
              placeholder="john.smith@example.com"
            />
            <div className="flex flex-col gap-3 md:flex-row md:items-start">
              <FormRoleField control={form.control} />
              <div className="flex gap-3">
                <FormInput
                  className="flex-1 sm:w-36"
                  inputClassName="uppercase"
                  control={form.control}
                  name="initials"
                  label="Initials"
                  placeholder="JS"
                />
                <Button
                  type="submit"
                  className="mt-5.5"
                  isLoading={isFetchingUserId}
                >
                  <Plus className="size-4" />
                  Add
                </Button>
              </div>
            </div>
          </form>
        </Form>

        {members.length > 0 ? (
          <div className="border rounded-md overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Initials</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {members.map((member, index) => (
                  <TableRow key={index}>
                    <TableCell>{member.email}</TableCell>
                    <TableCell>{member.initials}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {member.roles.map((role) => (
                          <Badge key={role}>{roundMemberRoleLabel[role]}</Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        type="button"
                        variant="destructive-quiet"
                        size="sm"
                        onClick={() => handleRemoveMember(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                        <span className="sr-only">Remove</span>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="border border-dashed rounded-md bg-muted px-4 py-8 flex flex-col gap-1 text-center text-muted-foreground">
            <p>No team members added yet</p>
            <p className="text-sm">
              Add team members to collaborate on this round
            </p>
          </div>
        )}
      </CardContent>

      <CardFooter className="flex justify-between gap-3">
        <Button type="button" variant="nav" onClick={onBack}>
          <ChevronLeftIcon className="size-4" />
          <span className="sr-only sm:not-sr-only">Back</span>
        </Button>
        <Button
          type="button"
          disabled={!hasFunder}
          disabledReason="Please add at least one funder"
          isLoading={isSubmitting}
          onClick={handleSubmit}
        >
          <SendIcon className="size-4" />
          Complete Setup
        </Button>
      </CardFooter>
    </Card>
  );
}
